'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ArrowSquareOut, BookOpen, FileText, Printer } from '@phosphor-icons/react';
import { Streamdown } from 'streamdown';

interface DocumentArtifactRendererProps {
  input?: Record<string, unknown>;
  output?: unknown;
  className?: string;
  displayContext?: 'inline' | 'artifact';
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function asString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function printMarkdown(title: string | undefined, markdown: string) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  const escaped = markdown
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  win.document.open();
  win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title ? title.replace(/[<>]/g, '') : 'Document'}</title>
<style>
  body { margin: 0; padding: 32px; color: #111827; font: 14px/1.6 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
  pre { white-space: pre-wrap; word-break: break-word; }
  @media print { body { padding: 0; } }
</style>
</head>
<body><pre>${escaped}</pre></body>
</html>`);
  win.document.close();
  win.addEventListener('load', () => {
    setTimeout(() => {
      win.focus();
      win.print();
    }, 150);
  });
}

export function DocumentArtifactRenderer({
  output,
  className,
  displayContext = 'inline',
}: DocumentArtifactRendererProps) {
  const record = asRecord(output) ?? {};
  const documents = Array.isArray(record.documents) ? record.documents : [];
  const title = asString(record.documentName) ?? 'Document';
  const markdown = asString(record.markdown) ?? asString(record.content) ?? '';
  const sourceUrl = asString(record.sourceUrl);
  const truncated = record.truncated === true;
  const meta = [
    asString(record.provider),
    asString(record.tier),
    typeof record.pageCount === 'number' ? `${record.pageCount} pages` : undefined,
    typeof record.markdownCharCount === 'number' ? `${record.markdownCharCount.toLocaleString()} chars` : undefined,
  ].filter(Boolean).join(' · ');

  if (documents.length > 0) {
    return (
      <article className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background',
        displayContext === 'artifact' ? 'h-full rounded-none border-0' : 'max-h-[520px]',
        className,
      )}>
        <header className="flex min-h-12 items-center justify-between gap-3 border-b bg-muted/30 px-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="grid size-8 shrink-0 place-items-center rounded-md border bg-background text-muted-foreground">
              <BookOpen weight="duotone" className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">Documents</p>
              <p className="truncate text-xs text-muted-foreground">{documents.length} available</p>
            </div>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <div className="space-y-2">
            {documents.map((item, index) => {
              const doc = asRecord(item) ?? {};
              const name = asString(doc.name) ?? `Document ${index + 1}`;
              const mimeType = asString(doc.mimeType);
              const summary = asString(doc.cachedSummary);
              return (
                <div key={asString(doc.id) ?? name} className="rounded-lg border bg-background p-3">
                  <div className="flex items-start gap-3">
                    <FileText weight="duotone" className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{name}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {[mimeType, doc.hasParsedMarkdown ? 'parsed' : 'not parsed'].filter(Boolean).join(' · ')}
                      </p>
                      {summary && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{summary}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </article>
    );
  }

  if (!markdown) {
    return (
      <div className={cn('rounded-lg border bg-muted/20 p-4', className)}>
        <div className="flex items-start gap-3">
          <FileText weight="duotone" className="mt-0.5 size-5 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">{title}</p>
            <p className="mt-1 text-sm text-muted-foreground">No parsed Markdown was returned for this document.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <article className={cn(
      'flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background',
      displayContext === 'artifact' ? 'h-full rounded-none border-0' : 'max-h-[620px]',
      className,
    )}>
      <header className="flex min-h-12 items-center justify-between gap-3 border-b bg-muted/30 px-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid size-8 shrink-0 place-items-center rounded-md border bg-background text-muted-foreground">
            <BookOpen weight="duotone" className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{title}</p>
            {meta && <p className="truncate text-xs text-muted-foreground">{meta}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {truncated && (
            <span className="hidden rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-600 dark:text-amber-300 sm:inline">
              truncated
            </span>
          )}
          <Button variant="ghost" size="icon" className="size-8" title="Print or save as PDF" onClick={() => printMarkdown(title, markdown)}>
            <Printer weight="bold" className="size-4" />
          </Button>
          {sourceUrl && (
            <Button asChild variant="ghost" size="icon" className="size-8" title="Open source file">
              <a href={sourceUrl} target="_blank" rel="noreferrer">
                <ArrowSquareOut weight="bold" className="size-4" />
              </a>
            </Button>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="prose prose-sm max-w-none dark:prose-invert prose-pre:whitespace-pre-wrap prose-pre:break-words">
          <Streamdown>{markdown}</Streamdown>
        </div>
      </div>
    </article>
  );
}
